import {
  Box,
  Alert,
  Link,
  MenuItem,
  Button,
  Typography,
} from '@mui/material'
import { 
  DataGridPro, 
  GridColDef,
  GridRenderCellParams, 
  GridRowId,
  GridValueFormatterParams,
  GridValueGetterParams, 
} from '@mui/x-data-grid-pro';
import {Link as RouterLink} from 'react-router-dom'
import Loading from 'components/Loading'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { isEmpty } from 'lodash'
import Invoice from 'logic/Invoice'
import React from 'react'
import { InvoiceSearchForm } from 'types/interfaces'
import FormField from 'components/FormField'
import PracticeGroup from 'logic/PracticeGroup'
import { adminBillingAccountDetailURL } from 'routes/urls'
import ExportOnlyToolBar from 'components/ExportToolbar';
import update from 'immutability-helper'

interface InvoicesTableProps {
  isAdmin?: boolean
  form: InvoiceSearchForm
  updateForm: (name: string, value: any) => void
  setForm: (form: InvoiceSearchForm) => void
}

type InvoiceType = 'late-fees' | 'job-payments' | 'all'

const InvoicesTable: React.FC<InvoicesTableProps> = ({ form, updateForm, setForm, isAdmin = false }) => {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const [selection, setSelection] = React.useState<GridRowId[]>([])
  const [records, setRecords] = React.useState<Invoice[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)
  const [practiceGroups, setPracticeGroups] = React.useState<PracticeGroup[]>([])

  const fetchPracticeGroups = React.useCallback(async () => {
    setPracticeGroups(await api.listPracticeGroup())
  }, [api])

  const fetchRecords = React.useCallback(async () => {
    try {
      setLoading(true)
      setRecords(await api.listInvoices(form))
    } catch (e) {
      showAlert('error', 'Error fetching invoices')
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [api, form, showAlert])

  const invoiceType: InvoiceType = React.useMemo(() => {
    if (form.is_late_fees && form.is_job_payments) {
      return 'all'
    } else if (form.is_late_fees) {
      return 'late-fees'
    } else if (form.is_job_payments) {
      return 'job-payments'
    } else {
      return 'all'
    }
  }, [form])

  const updateInvoiceType = React.useCallback((value: InvoiceType) => {
    if (value === 'late-fees') {
      setForm(update(form, { 
        is_late_fees: { $set: true }, 
        is_job_payments: { $set: false } 
      }))
    } else if (value === 'job-payments') {
      setForm(update(form, { 
        is_late_fees: { $set: false }, 
        is_job_payments: { $set: true } 
      }))
    } else {
      setForm(update(form, { 
        $unset: ['is_late_fees', 'is_job_payments'],
      }))
    }
  }, [setForm, form])

  const getInvoiceActions = React.useCallback((record: Invoice) => {
    if (record.status === 'open') {
      return (
        <Box sx={{display: 'flex', gap: 1}}>
          <Link component='a' href={record.invoice_pdf} target="_blank">View</Link>
          <Link component='a' href={record.hosted_invoice_url} target="_blank">
            {record.charge_error ? 'Retry Payment' : 'Pay'}
          </Link>
        </Box>
      )
    } else {
      return (
        <Link component='a' href={record.invoice_pdf} target="_blank">View</Link>
      )
    }
  }, [])

  const onSelectionChanged = React.useCallback((selectedIDs: GridRowId[]) => {
    setSelection(selectedIDs)
  }, [setSelection])
  
  const columns: GridColDef<Invoice>[] = React.useMemo(() => {
    const adminColumns: GridColDef<Invoice>[] = [{
      minWidth: 150,
      headerName: 'Name',
      field: 'billing_name',
      type: 'string',
      renderCell: (params: GridRenderCellParams<Invoice>) => {
        const record = params.row
        return (
          <Link component={RouterLink} to={adminBillingAccountDetailURL(record.billing_account_id)}>
            { record.billing_name }
          </Link>
        )
      }
    }, {
      minWidth: 150,
      headerName: 'Email',
      field: 'billing_email',
      type: 'string',
    }, {
      minWidth: 100,
      headerName: 'Practice',
      field: 'practice_name',
      type: 'string',
    }, {
      minWidth: 100,
      headerName: 'Practice Group',
      field: 'practice_group_label',
      type: 'string',
    }, {
      minWidth: 150,
      headerName: 'Send payments to',
      field: 'account_number',
      type: 'string',
      renderCell: (params: GridRenderCellParams<Invoice>) => {
        return (
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            <Typography variant='caption'>Account: {params.row.account_number}</Typography>
            <Typography variant='caption'>Sort Code: {params.row.sort_code}</Typography>
          </Box>
        )
      }
    }]
    const userColumns: GridColDef<Invoice>[] = [{
      minWidth: 100,
      headerName: 'Invoice',
      field: 'invoice_number',
      type: 'string',
    }, {
      minWidth: 300,
      headerName: 'Description',
      field: 'description',
      type: 'string',
    }, {
      minWidth: 50,
      headerName: 'Sent At',
      field: 'created_at',
      type: 'dateTime',
      valueGetter: (params: GridValueGetterParams<Invoice>) => {
        return params.row.created_at?.toJSDate()
      },
      renderCell: (params: GridRenderCellParams<Invoice>) => {
        const record = params.row
        return record.created_at_label()
      }
    }, {
      minWidth: 50,
      headerName: 'Amount Due',
      field: 'amount_due',
      valueFormatter: (params: GridValueFormatterParams<number>) => {
        return params.value / 100
      },
      renderCell: (params: GridRenderCellParams<Invoice>) => {
        const record = params.row
        return record.amount_label
      }
    }, {
      minWidth: 50,
      headerName: 'Status',
      field: 'status_label',
      type: 'string',
    }, {
      minWidth: 100,
      headerName: 'Actions',
      field: 'actions',
      type: 'string',
      renderCell: (params: GridRenderCellParams<Invoice>) => {
        const record = params.row
        return getInvoiceActions(record)
      }
    }]
    return isAdmin ? adminColumns.concat(userColumns) : userColumns
  }, [isAdmin, getInvoiceActions])

  React.useEffect(() => {
    fetchPracticeGroups()
  }, [fetchPracticeGroups])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
        {isAdmin ? (
          <FormField
            select
            name='practice_group'
            label='Practice Group'
            sx={{ width: 150 }}
            onChange={(e) => updateForm('practice_group_id', e.target.value)}
            value={form.practice_group_id ?? ''}>
            <MenuItem value=''>Not Specified</MenuItem>
            <MenuItem value='-999'>Standalone</MenuItem>
            {practiceGroups.map((group) => (
              <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
            ))}
          </FormField>
        ) : null}
        <FormField
          select
          name='status'
          label='Status'
          sx={{ width: 150 }}
          onChange={(e) => updateForm('status', e.target.value)}
          value={form.status ?? ''}>
          <MenuItem value=''>Not Specified</MenuItem>
          <MenuItem value='draft'>draft</MenuItem>
          <MenuItem value='open'>open</MenuItem>
          <MenuItem value='paid'>paid</MenuItem>
          <MenuItem value='uncollectible'>uncollectible</MenuItem>
          <MenuItem value='void'>void</MenuItem>
        </FormField>
        <FormField
          select
          name='invoice_type'
          label='Invoice Type'
          sx={{ width: 150 }}
          onChange={(e) => updateInvoiceType(e.target.value as InvoiceType)}
          value={invoiceType}>
          <MenuItem value='late-fees'>Late Fees</MenuItem>
          <MenuItem value='job-payments'>Job Payments</MenuItem>
          <MenuItem value='all'>Show All</MenuItem>
        </FormField>
        <Button
          variant="contained"
          onClick={fetchRecords}
          disabled={loading}>
          {loading ? 'Please Wait ...' : 'Search'}
        </Button>
        <Typography variant='caption' color='error'>
          Records won't load until you click the Search button
        </Typography>
      </Box>
      {loading ? (
        <Loading />
      ) : isEmpty(records) ? (
        <Alert severity='info'>No Records Found</Alert>
      ) : (
        <React.Fragment>
          <Alert severity='info'>{records.length} records returned. {selection.length} selected</Alert>
          <DataGridPro 
            rows={records}
            columns={columns}
            getRowId={(record) => record.id}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selection}
            onRowSelectionModelChange={onSelectionChanged}
            loading={loading}
            hideFooter
            slots={{ toolbar: ExportOnlyToolBar }}
          />
        </React.Fragment>
      )}
    </Box>
  )
}

export default InvoicesTable